'use client';

import { isHiddenBuilderMessage } from '@/lib/builder/dani/chat';
import { handoffDisplay, isHiddenFeasibilityMessage } from '@/lib/feasibility/dani/primer-marks';
import { isHiddenCrosstabMessage } from '@/lib/crosstab/dani/primer-marks';
import PulseLoader from '@/@studio/components/PulseLoader';
import { crosstabService } from '@/@crosstab/services';
import { buildCrosstabContext } from '../utils/crosstab-payload';
import { engineCrosstabContext, inventoryOnly } from '../utils/engine-crosstab-context';
import { showErrorToast } from '@/components/ui/molecules/Toast';
import { useSession } from '@/providers/enhanced-session';
import { UserProfile } from '@/types';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { ParsedIoohCrosstab } from '../utils/iooh-crosstab-parser';
import type { IoohStudyDetails } from './slide-editor/iooh-slide-schema';
import { clientNameFromAnswers } from './slide-editor/iooh-campaign';
import { AgentModes, ROLE_TYPES, STATUS_MESSAGES, STREAMING_EVENT_TYPES } from '../constants';
import { useStudioAI } from '../context';
import { useAutoScroll } from '../hooks';
import type { ChatMessagesProps } from '../props';
import type { ChatMessageContent, ContextDataEntry } from '../types';
import { getCrosstabSessionData } from '../hooks/useActivePlannerReports';
import { looksLikePlannerJson, parseAllPlannerCards } from '../utils';
import { ContextDivider } from './context-divider';
import { Message } from './message';
import { PlannerMessage } from './planner-mode';
import { StatusMessage } from './status-message';

function PureChatMessages({
    messages,
    conversationHistoryLoading = false,
    className = '',
    style,
    constrainWidth = true,
    onScrollStateChange,
    scrollToBottomRef,
    scrollToTopRef,
    startAtTop = false,
    onSelectOption,
    onSelectMultiple,
    onSkipOptions,
}: ChatMessagesProps) {
    const {
        chatState,
        currentStatus,
        isLoading,
        currentConversationId,
        currentThreadId,
        submitFeedback,
        allUserFeedback,
        cautionToolNames,
        forkConversationFromMessage,
        plannerFormState,
        plannerTemplateId,
        agentMode,
        effectiveAgentMode,
        updateMessage,
        setCurrentStatus,
        setIsLoading,
        sendSilentMessage,
        pendingOptions,
        handleReportKickedOff,
        planProgress,
        conversationContextData,
    } = useStudioAI();
    const router = useRouter();
    const { data: session } = useSession();
    const user = session?.user as UserProfile;

    const { scrollContainerRef, contentRef } = useAutoScroll({
        messages,
        conversationHistoryLoading,
        isLoading,
        onScrollStateChange,
        scrollToBottomRef,
        scrollToTopRef,
        startAtTop,
    });

    const latestAssistantMessage = messages
        .filter((msg) => msg.role === ROLE_TYPES.ASSISTANT)
        .pop();
    const latestAssistantMessageId = latestAssistantMessage?.id;

    // Once true, suppresses the CrosstabPicker for the remainder of this session
    // (until the component remounts on a new conversation). Tracks by boolean
    // rather than message ID so the picker stays hidden even when DANI responds
    // with a new message that would otherwise re-enable it.
    const [crosstabStepSubmitted, setCrosstabStepSubmitted] = useState(false);
    const latestAssistantMessageIdRef = useRef(latestAssistantMessageId);
    latestAssistantMessageIdRef.current = latestAssistantMessageId;

    const contextDividers = useMemo(() => {
        const dividers = new Map<number, ContextDataEntry>();
        let lastContextKey: string | null | undefined = undefined;
        messages.forEach((message, index) => {
            if (message.role !== ROLE_TYPES.USER) return;
            const currentKey: string | null = message.context_key ?? null;
            if (currentKey !== lastContextKey) {
                lastContextKey = currentKey;
                if (currentKey && conversationContextData?.contexts[currentKey]) {
                    dividers.set(index, conversationContextData.contexts[currentKey]);
                }
            }
        });
        return dividers;
    }, [messages, conversationContextData]);

    const handleAnimationComplete = useCallback(
        (messageId: string) => updateMessage(messageId, { _animate: false }),
        [updateMessage],
    );

    const handleCardSendMessage = useCallback(
        async (text: string, contextData?: Record<string, unknown>) => {
            const isPlanner = effectiveAgentMode === AgentModes.PLANNER;

            // "Build a Report from this" on PlanSummaryCard lands here.
            const action = contextData?.action;
            const runIdFromCtx =
                typeof contextData?.runId === 'string' ? contextData.runId : undefined;
            if (action === 'run_report' && runIdFromCtx) {
                const plannerFormStateCtx = contextData?.plannerFormState as
                    | Record<string, unknown>
                    | undefined;
                const brandLabel =
                    (typeof plannerFormStateCtx?.brand === 'string' && plannerFormStateCtx.brand) ||
                    (typeof contextData?.brand === 'string' && contextData.brand) ||
                    undefined;
                // PlanSummaryCard passes output_format at the top level of contextData.
                const outputFormat =
                    contextData?.output_format === 'pptx' ||
                    plannerFormStateCtx?.output_format === 'pptx'
                        ? ('pptx' as const)
                        : ('pdf' as const);

                handleReportKickedOff(runIdFromCtx, brandLabel || undefined, outputFormat);
                // Bridge the gap until the silent "Run report" message lands
                // on the streaming layer, which then takes over status/loading.
                setCurrentStatus(
                    `${STATUS_MESSAGES.THINKING.message}|${STATUS_MESSAGES.THINKING.icon}`,
                );
                setIsLoading(true);

                // iOOH shortcut: if an iOOH Excel is cached for this conversation
                // and the user chose PPTX, navigate directly to the template editor
                // instead of sending to Cortex for report generation.
                if (outputFormat === 'pptx' && currentConversationId) {
                    let ioohCrosstabRaw: string | null = null;
                    try {
                        ioohCrosstabRaw = localStorage.getItem(
                            `iooh-crosstab-${currentConversationId}`,
                        );
                    } catch {}
                    if (ioohCrosstabRaw) {
                        try {
                            const existingStudy = localStorage.getItem(
                                `iooh-study-${currentConversationId}`,
                            );
                            if (!existingStudy) {
                                const parsedCrosstab = JSON.parse(
                                    ioohCrosstabRaw,
                                ) as ParsedIoohCrosstab;
                                const firstSection = Object.values(parsedCrosstab.sections)[0];
                                const brandName = plannerFormState?.brand ?? brandLabel ?? 'Brand';
                                const studyDetails: IoohStudyDetails = {
                                    brandName,
                                    productCategory:
                                        (plannerFormState?.['product_category'] as string) ?? '',
                                    // The campaign step, asked as Client Name (BT-2070); never
                                    // the brand (M4S-5626, BT-2053).
                                    clientName: clientNameFromAnswers(plannerFormState),
                                    reportDate: new Date().toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric',
                                    }),
                                    fieldworkDatesExposed:
                                        (plannerFormState?.['fieldwork_dates_exposed'] as string) ??
                                        'TBD',
                                    fieldworkDatesUnexposed:
                                        (plannerFormState?.[
                                            'fieldwork_dates_unexposed'
                                        ] as string) ?? 'TBD',
                                    markets: (plannerFormState?.['markets'] as string) ?? 'TBD',
                                    venueTypes:
                                        (plannerFormState?.['venue_types'] as string) ?? 'TBD',
                                    sampleUnexposed: firstSection?.bases.unexposed ?? 300,
                                    sampleExposed: firstSection?.bases.exposed ?? 300,
                                    marketLevelAnalysis:
                                        plannerFormState?.['market_level_analysis'] === 'yes',
                                };
                                localStorage.setItem(
                                    `iooh-study-${currentConversationId}`,
                                    JSON.stringify(studyDetails),
                                );
                            }
                        } catch {}
                        router.push(`/dani/${currentConversationId}/ai/iooh`);
                        return;
                    }
                }
            }

            // Crosstab path: fetch the full data and combine it with the
            // crosstab_id/name into a single message so DANI receives everything
            // before emitting thread_metadata (runId). Splitting into two messages
            // caused DANI to kick off report generation before the data arrived.
            // Strip `responses` (raw per-respondent rows) to keep payload size manageable.
            // Await the send so polling only starts if the message actually succeeds.
            let isCrosstabPayload = false;
            try {
                const parsed = JSON.parse(text) as Record<string, unknown>;
                const fields = parsed?.fields as Record<string, unknown> | undefined;
                if (parsed?.action === 'planner_step_fields' && fields?.crosstab_id != null) {
                    isCrosstabPayload = true;
                    setCrosstabStepSubmitted(true);
                    const crosstabId = fields.crosstab_id as string;

                    setCurrentStatus(
                        `${STATUS_MESSAGES.GATHERING_DATA.message}|${STATUS_MESSAGES.GATHERING_DATA.icon}`,
                    );
                    setIsLoading(true);

                    // Skip API call when data is already embedded (e.g. uploaded Excel files).
                    let enrichedFields: Record<string, unknown> | undefined;
                    if (fields.crosstab_data != null) {
                        enrichedFields = fields;
                    } else {
                        // M4S-5777 X125: a saved AI crosstab hands DANI the engine's figures,
                        // computed on the server from its record, so the report quotes the table
                        // instead of working numbers out itself.
                        const engine = await engineCrosstabContext(crosstabId).catch(() => null);
                        if (engine) {
                            enrichedFields = { ...fields, crosstab_data: JSON.stringify(engine) };
                        }
                    }
                    if (enrichedFields === undefined) {
                        // A classic crosstab: its question inventory, labelled as one (no figures).
                        const { responses: _responses, ...rawData } =
                            await crosstabService.loadData(crosstabId);
                        if (process.env.NODE_ENV === 'development') {
                            console.log(
                                '[DANI crosstab] loadData questions:',
                                rawData.questions?.length ?? 0,
                                'surveyId:',
                                rawData.surveyId ?? '(none)',
                                'projectId:',
                                rawData.projectId,
                            );
                        }
                        enrichedFields = {
                            ...fields,
                            crosstab_data: JSON.stringify(
                                inventoryOnly(buildCrosstabContext(rawData)),
                            ),
                        };
                    }

                    const promptToSend = JSON.stringify({ ...parsed, fields: enrichedFields });
                    if (process.env.NODE_ENV === 'development') {
                        console.log(
                            '[DANI crosstab] payload bytes:',
                            new Blob([promptToSend]).size,
                        );
                    }
                    await sendSilentMessage({
                        prompt: promptToSend,
                        parentMessageId: '0',
                        agentMode: isPlanner ? AgentModes.PLANNER : undefined,
                        conversationId: currentConversationId ?? undefined,
                        templateId:
                            isPlanner && plannerTemplateId != null ? plannerTemplateId : undefined,
                        contextData,
                    });
                    return;
                }
            } catch (err) {
                if (isCrosstabPayload) {
                    const msg = err instanceof Error ? err.message : 'Failed to send crosstab data';
                    showErrorToast('Crosstab error', msg);
                    return;
                }
                // JSON parse failure: not a crosstab payload, fall through to normal send.
            }

            // For run_report via PlanSummaryCard, enrich contextData with
            // plannerFormState (including crossTabData) if not already present.
            let enrichedContextData = contextData;
            if (action === 'run_report' && currentConversationId) {
                const cachedData = getCrosstabSessionData(currentConversationId);
                if (cachedData && !contextData?.plannerFormState) {
                    const outputFmt =
                        contextData?.output_format === 'pptx' ? 'pptx' : ('pdf' as const);
                    enrichedContextData = {
                        ...contextData,
                        plannerFormState: {
                            output_format: outputFmt,
                            submitted: true,
                            crosstab_data: cachedData,
                            crossTabData: cachedData,
                        },
                    };
                }
            }

            try {
                await sendSilentMessage({
                    prompt: text,
                    parentMessageId: '0',
                    agentMode: isPlanner ? AgentModes.PLANNER : undefined,
                    conversationId: currentConversationId ?? undefined,
                    templateId:
                        isPlanner && plannerTemplateId != null ? plannerTemplateId : undefined,
                    contextData: enrichedContextData,
                });
            } catch (err) {
                // If the report was already kicked off (runId tracked), a network
                // hiccup on the follow-up silent message is harmless — polling will
                // pick up the report status. Only surface the error for non-report sends.
                if (action === 'run_report' && runIdFromCtx) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(
                            '[DANI run_report] network error suppressed — report already tracked',
                            err,
                        );
                    }
                    return;
                }
                const msg = err instanceof Error ? err.message : 'Failed to send message';
                showErrorToast('Network error', msg);
            }
        },
        [
            sendSilentMessage,
            effectiveAgentMode,
            currentConversationId,
            plannerTemplateId,
            plannerFormState,
            router,
            handleReportKickedOff,
            setCurrentStatus,
            setIsLoading,
        ],
    );

    const [statusMessage] = currentStatus ? currentStatus.split('|') : [null];

    // Echo the last non-silent user message so the StatusMessage can show it
    // while Claude is processing ("Thinking... / "can you create me an iOOH crosstab?"")
    const lastUserEcho = useMemo(() => {
        const last = [...messages]
            .reverse()
            .find(
                (m) =>
                    m.role === ROLE_TYPES.USER &&
                    !m.silent &&
                    !isHiddenFeasibilityMessage(m) &&
                    !isHiddenCrosstabMessage(m) &&
                    !isHiddenBuilderMessage(m),
            );
        if (!last) return undefined;
        const handoff = handoffDisplay(last.content);
        if (handoff) return handoff;
        if (typeof last.content === 'string') return last.content.trim() || undefined;
        if (Array.isArray(last.content)) {
            const textPart = (last.content as ChatMessageContent[]).find(
                (c) => c.type === STREAMING_EVENT_TYPES.TEXT,
            );
            return (textPart?.text ?? '').trim() || undefined;
        }
        return undefined;
    }, [messages]);

    const streamingThinkingText = useMemo(() => {
        const streamingMsg = messages.find(
            (msg) => msg.role === ROLE_TYPES.ASSISTANT && msg.isStreaming,
        );
        if (!streamingMsg || !Array.isArray(streamingMsg.content)) return undefined;
        const thinkingParts = (streamingMsg.content as ChatMessageContent[])
            .filter((c) => c.type === STREAMING_EVENT_TYPES.THINKING)
            .map((c) => c.thinking?.text || c.text || '')
            .filter((t) => t.length > 0);
        return thinkingParts.length > 0 ? thinkingParts.join('\n\n') : undefined;
    }, [messages]);

    return (
        <div
            ref={scrollContainerRef}
            className={`relative flex min-w-0 flex-1 flex-col overflow-y-auto ${className}`}
            style={style}
        >
            <div
                ref={contentRef}
                className={`flex flex-col gap-1 pb-20 pt-6 ${
                    constrainWidth ? 'mx-auto w-full max-w-3xl' : ''
                }`}
            >
                {conversationHistoryLoading && messages.length === 0 && (
                    <PulseLoader title="conversation" />
                )}

                {messages.map((message, msgIndex) => {
                    // A feasibility or Builder conversation's opening rules and Studio's messages to DANI are never shown.
                    if (
                        message.silent ||
                        isHiddenFeasibilityMessage(message) ||
                        isHiddenCrosstabMessage(message) ||
                        isHiddenBuilderMessage(message)
                    )
                        return null;
                    // A check's handoff to DANI shows only its line, as when it was sent.
                    const handoff =
                        message.role === ROLE_TYPES.USER ? handoffDisplay(message.content) : null;
                    const shown = handoff
                        ? {
                              ...message,
                              content:
                                  typeof message.content === 'string'
                                      ? handoff
                                      : [{ type: STREAMING_EVENT_TYPES.TEXT, text: handoff }],
                          }
                        : message;

                    const contextDivider = contextDividers.get(msgIndex);

                    const isPlannerContent =
                        message.role === ROLE_TYPES.ASSISTANT &&
                        (looksLikePlannerJson(message.content) ||
                            (!message.isStreaming &&
                                parseAllPlannerCards(message.content, message.metadata).length >
                                    0));

                    if (isPlannerContent) {
                        return (
                            <div key={`${currentThreadId}-${message.id}`} className="w-full">
                                {contextDivider && (
                                    <ContextDivider
                                        contextEntry={contextDivider}
                                        className="px-0"
                                    />
                                )}
                                <PlannerMessage
                                    message={message}
                                    threadId={currentThreadId}
                                    messages={messages}
                                    msgIndex={msgIndex}
                                    plannerFormState={plannerFormState}
                                />
                            </div>
                        );
                    }

                    const isLatestAssistant = message.id === latestAssistantMessageId;
                    const currentStepId =
                        planProgress?.steps[(planProgress?.currentStep ?? 0) - 1]?.id;
                    const isCrosstabStep =
                        isLatestAssistant &&
                        currentStepId === 'crosstab_retrieval_by_org' &&
                        !crosstabStepSubmitted;

                    // Find the project search hint so CrosstabPicker can scope to that project.
                    // Strategy 1: scan message history for a plan_card_step that has search_hint.
                    // Strategy 2: scan planProgress step answers for a YYYY-MM-NNNNN project code.
                    let crosstabSearchHint: string | undefined;
                    if (isCrosstabStep) {
                        for (const msg of messages) {
                            const cards = parseAllPlannerCards(msg.content, msg.metadata);
                            for (const card of cards) {
                                const s = card.plan.steps?.find(
                                    (st) => st.id === 'crosstab_retrieval_by_org' && st.search_hint,
                                );
                                if (s?.search_hint) {
                                    crosstabSearchHint = s.search_hint;
                                    break;
                                }
                            }
                            if (crosstabSearchHint) break;
                        }
                        if (!crosstabSearchHint && planProgress?.steps) {
                            const stepIdx = (planProgress.currentStep ?? 0) - 1;
                            for (let i = 0; i < stepIdx; i++) {
                                const ans = planProgress.steps[i]?.answer;
                                if (ans) {
                                    const m = ans.match(/(\d{4}-\d{2}-\d+)/);
                                    if (m) {
                                        crosstabSearchHint = m[1];
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    return (
                        <div key={`${currentThreadId}-${message.id}`} className="w-full">
                            {contextDivider && (
                                <ContextDivider contextEntry={contextDivider} className="px-0" />
                            )}
                            <Message
                                message={shown}
                                user={user}
                                chatState={chatState}
                                isLatestAssistantMessage={isLatestAssistant}
                                currentConversationId={currentConversationId}
                                currentThreadId={currentThreadId}
                                submitFeedback={submitFeedback}
                                showFeedbackSubmit={true}
                                allUserFeedback={allUserFeedback}
                                cautionToolNames={cautionToolNames}
                                onStartNewChatFromMessage={forkConversationFromMessage}
                                agentMode={agentMode}
                                onAnimationComplete={handleAnimationComplete}
                                onSendMessage={handleCardSendMessage}
                                pendingOptions={isLatestAssistant ? pendingOptions : null}
                                onSelectOption={isLatestAssistant ? onSelectOption : undefined}
                                onSelectMultiple={isLatestAssistant ? onSelectMultiple : undefined}
                                onSkipOptions={isLatestAssistant ? onSkipOptions : undefined}
                                isCrosstabStep={isCrosstabStep}
                                crosstabSearchHint={isCrosstabStep ? crosstabSearchHint : undefined}
                            />
                        </div>
                    );
                })}

                {isLoading && (
                    <div className="w-full px-4 py-2">
                        <StatusMessage
                            message={statusMessage ?? STATUS_MESSAGES.THINKING.message}
                            isInternal={user?.isInternal}
                            thinkingText={streamingThinkingText}
                            userEcho={lastUserEcho}
                            showElapsed
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export const ChatMessages = memo(PureChatMessages, (prevProps, nextProps) => {
    if (prevProps.messages.length !== nextProps.messages.length) return false;
    if (prevProps.conversationHistoryLoading !== nextProps.conversationHistoryLoading) return false;
    if (prevProps.style !== nextProps.style) return false;
    if (prevProps.constrainWidth !== nextProps.constrainWidth) return false;
    if (prevProps.startAtTop !== nextProps.startAtTop) return false;

    for (let i = 0; i < prevProps.messages.length; i++) {
        const prevMessage = prevProps.messages[i];
        const nextMessage = nextProps.messages[i];
        if (prevMessage !== nextMessage) return false;
    }

    return true;
});
